import React, { ReactNode, useState, useMemo } from "react"
import {
    ActionIcon,
    createStyles,
    Flex,
    SegmentedControl,
    Spoiler,
    Stack,
    Tooltip,
    useMantineTheme,
    Text,
    Divider,
} from "@mantine/core"
import Markdown from "react-markdown"

import { Icon } from "quorumdesign"
import { Button } from "@quorumus/plum"
import { PersonalizedSummary } from "@/components/PersonalizedSummary/PersonalizedSummary"
import { CustomDescription } from "@/pages/ai-bill-tracker/components/CustomDescription"
import { useCustomDescriptionQuery } from "@/pages/ai-bill-tracker/hooks/useCustomDescription"
import { useClipboard } from "@mantine/hooks"
import { hasAnyPermission } from "shared/imports/permissionFunctions"
import { PenToSquareIcon } from "@quorumus/plum/dist/components/Icons"

const useStyles = createStyles((theme) => ({
    alert: {
        borderRadius: "8px",
        border: `1px solid ${theme.colors.violet[3]}`,
        backgroundColor: theme.colors.violet[0],
        padding: "16px",
        alignSelf: "stretch",
    },
    loading: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height: "100%",
        width: "100%",
        gap: "1rem",
        padding: "1rem",
    },
    divider: {
        width: "100%",
        height: "1px",
        backgroundColor: "#dee2e6",
        margin: "16px 0 0 0",
    },
}))

const SummarySingleSentenceAlert = ({ children }) => {
    const { classes } = useStyles()
    return <div className={classes.alert}>{children}</div>
}

// extractText is used to get the text when user try to copy the content
const extractText = (node: ReactNode): string => {
    if (node === null || node === undefined || typeof node === "boolean") return ""
    if (typeof node === "string" || typeof node === "number") return node.toString()
    if (Array.isArray(node)) return node.map((child) => extractText(child)).join("")
    if (React.isValidElement(node)) return extractText(node.props.children)
    return ""
}

export const SummarySectionWithPersonalizedTab = ({
    aiSummary,
    aiSummaryDate,
    aiSummarySingleSentence,
    personalizedSummary,
    profileId,
    billId,
    boardId,
    nextBillId,
    officialSummary,
    mostRecentBillText,
    isBillDataType,
}) => {
    const theme = useMantineTheme()
    const { classes } = useStyles()

    const hasProfile = boardId && profileId
    const isPersonalizedSummaryEnabled =
        hasAnyPermission(DjangIO.app.models.PermissionType.ai_personalized_summaries) && isBillDataType

    const { data: customDescription } = useCustomDescriptionQuery(billId, {
        enabled: isBillDataType && !!boardId,
    })

    // State for custom description editing
    const [isEditingCustomDescription, setIsEditingCustomDescription] = useState(false)
    const [customDescriptionText, setCustomDescriptionText] = useState("")

    const segmentedControlData = useMemo(() => {
        const tabs = []
        if (isPersonalizedSummaryEnabled && mostRecentBillText) {
            tabs.push({
                label: <span data-plum-id="bill-profile-personalized-summary">Personalized</span>,
                value: "personalized",
            })
        }
        if (aiSummary) {
            tabs.push({ label: "AI", value: "ai" })
        }
        if (officialSummary) {
            tabs.push({ label: "Official", value: "official" })
        }

        if (customDescription?.text && boardId) {
            tabs.push({ label: "Custom Description", value: "custom" })
        }

        // the bill can has text but no profile, in this case we don't show the personalized tab as default
        // and should show AI
        if (isPersonalizedSummaryEnabled && mostRecentBillText && hasProfile) {
            return { tabs, defaultTab: "personalized" }
        }
        if (aiSummary) {
            return { tabs, defaultTab: "ai" }
        }
        return { tabs, defaultTab: "official" }
    }, [
        mostRecentBillText,
        aiSummary,
        officialSummary,
        hasProfile,
        customDescription,
        boardId,
        isPersonalizedSummaryEnabled,
    ])

    const [currentValue, setCurrentValue] = useState(segmentedControlData.defaultTab)
    const clipboard = useClipboard({ timeout: 1500 })

    const showSegmentedControl = segmentedControlData.tabs.length > 1

    const handleAddCustomDescription = () => {
        setIsEditingCustomDescription(true)
    }

    return (
        <Stack spacing="xs">
            {showSegmentedControl && (
                <Flex align={"center"} gap={"8px"}>
                    <Text size="md" weight={500} color={theme.colors.gray[9]}>
                        Summaries:
                    </Text>
                    <SegmentedControl
                        data={segmentedControlData.tabs}
                        value={currentValue}
                        onChange={setCurrentValue}
                        size="xs"
                        // Isolate this component to avoid rendering on top of the NavigationTabs
                        style={{ isolation: "isolate" }}
                    />
                    {boardId && !customDescription?.text && !isEditingCustomDescription && (
                        <Button
                            variant="secondary"
                            size="sm"
                            onClick={handleAddCustomDescription}
                            data-plum-id="custom-description"
                        >
                            + Custom Description
                        </Button>
                    )}
                    {boardId && currentValue === "custom" && !isEditingCustomDescription && customDescription?.text && (
                        <Button
                            variant="tertiary"
                            size="md"
                            onClick={() => {
                                setIsEditingCustomDescription(true)
                                setCustomDescriptionText(customDescription.text)
                            }}
                            data-plum-id="edit-custom-description"
                        >
                            <PenToSquareIcon />
                        </Button>
                    )}
                </Flex>
            )}

            {currentValue === "ai" && (
                <>
                    {aiSummarySingleSentence && (
                        <SummarySingleSentenceAlert>
                            <strong>Key Takeaway: </strong>
                            {aiSummarySingleSentence}
                        </SummarySingleSentenceAlert>
                    )}

                    <Spoiler maxHeight={120} showLabel="See more" hideLabel="See less">
                        <Stack spacing="xs">
                            <Markdown>{aiSummary}</Markdown>
                            {aiSummaryDate && (
                                <Text size="sm" color="dimmed">
                                    Updated: {aiSummaryDate}
                                </Text>
                            )}
                        </Stack>
                    </Spoiler>
                </>
            )}

            {currentValue === "personalized" && (
                <PersonalizedSummary profileId={profileId} billId={billId} boardId={boardId} nextBillId={nextBillId} />
            )}

            {currentValue === "official" && (
                <Spoiler maxHeight={120} showLabel="See more" hideLabel="See less">
                    <Stack spacing="xs">{officialSummary}</Stack>
                </Spoiler>
            )}

            {(currentValue === "custom" || isEditingCustomDescription) && (
                <CustomDescription
                    billId={billId}
                    isEditing={isEditingCustomDescription}
                    onEditingChange={setIsEditingCustomDescription}
                    text={customDescriptionText}
                    onTextChange={setCustomDescriptionText}
                />
            )}

            <Tooltip label={clipboard.copied ? "Copied" : "Copy"} withArrow position="right">
                <ActionIcon
                    color={clipboard.copied ? "teal" : "gray"}
                    onClick={() => {
                        clipboard.copy(
                            {
                                ai: aiSummary,
                                official: extractText(officialSummary),
                                personalized: personalizedSummary,
                                custom: customDescriptionText,
                            }[currentValue],
                        )
                    }}
                    aria-label="Copy this message"
                    data-plum-id={
                        {
                            ai: "ai-summary-copy-button",
                            official: "official-summary-copy-button",
                            personalized: "personalized-summary-copy-button",
                            custom: "custom-description-copy-button",
                        }[currentValue]
                    }
                >
                    {clipboard.copied ? (
                        <Icon icon="check" size="sm" />
                    ) : (
                        <Icon icon="copy" size="sm" color={theme.colors.gray[7]} />
                    )}
                </ActionIcon>
            </Tooltip>
            <Divider className={classes.divider} />
        </Stack>
    )
}
